/**
 * Karte „Passwort ändern" (Kapitel 3.4, Entscheidung #31) — neuer
 * Profil-Menüpunkt für eingeloggte User. Ruft POST /api/auth/change-password
 * (aktuelles + neues Passwort) auf. Kein neues Backend-Modell nötig.
 */

import { useState, type FormEvent } from 'react';
import { useAuth, type AuthUser } from '../store/auth';
import { apiFetch, ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export function ChangePasswordCard() {
  const token = useAuth((s) => s.token);
  const [open, setOpen] = useState(false);
  if (!token) return null;

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Passwort</p>
        <button
          onClick={() => setOpen(true)}
          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5"
        >
          Ändern
        </button>
      </div>
      {open && <ChangePasswordModal token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ChangePasswordModal({ token, onClose }: { token: string; onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Neues Passwort: mindestens 8 Zeichen.');
      return;
    }
    if (newPassword !== newPassword2) {
      setError('Neue Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        token,
        body: { currentPassword, newPassword },
      });
      hapticSuccess();
      setDone(true);
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Passwort konnte nicht geändert werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Passwort ändern</h3>
        {done ? (
          <>
            <p className="text-sm text-emerald-400 mb-4">Passwort geändert.</p>
            <button onClick={onClose} className="btn w-full">
              Schließen
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Aktuelles Passwort</span>
              <input
                required
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
              />
            </label>
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Neues Passwort</span>
              <input
                required
                type="password"
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
            </label>
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Neues Passwort (wiederholen)</span>
              <input
                required
                type="password"
                minLength={8}
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-ghost flex-1">
                Abbrechen
              </button>
              <button type="submit" disabled={busy} className="btn flex-1">
                {busy ? 'Speichere…' : 'Ändern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Gesamtprüfung 11.09.2026: eigene Anmelde-E-Mail ändern (mit aktuellem
 * Passwort) — POST /api/auth/change-email. Die Mitarbeiterverwaltung ändert
 * E-Mail/Passwort der eigenen Person nicht mehr.
 */
export function ChangeEmailCard() {
  const token = useAuth((s) => s.token);
  const email = useAuth((s) => s.user?.email);
  const [open, setOpen] = useState(false);
  if (!token) return null;

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Anmelde-E-Mail</p>
          {email && <p className="text-xs text-slate-400 truncate">{email}</p>}
        </div>
        <button
          onClick={() => setOpen(true)}
          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5 shrink-0"
        >
          Ändern
        </button>
      </div>
      {open && <ChangeEmailModal token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ChangeEmailModal({ token, onClose }: { token: string; onClose: () => void }) {
  const setUser = useAuth((s) => s.setUser);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/auth/change-email', {
        method: 'POST',
        token,
        body: { currentPassword, newEmail },
      });
      setUser(data.user);
      hapticSuccess();
      setDone(true);
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'E-Mail konnte nicht geändert werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Anmelde-E-Mail ändern</h3>
        {done ? (
          <>
            <p className="text-sm text-emerald-400 mb-4">
              E-Mail geändert. Andere Geräte wurden abgemeldet.
            </p>
            <button onClick={onClose} className="btn w-full">
              Schließen
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Neue E-Mail</span>
              <input
                required
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="input"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Aktuelles Passwort</span>
              <input
                required
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-ghost flex-1">
                Abbrechen
              </button>
              <button type="submit" disabled={busy} className="btn flex-1">
                {busy ? 'Speichere…' : 'Ändern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
